/**
 * Shared source-document navigation (drill-down last hop).
 *
 * Maps an accounting/transaction `source_type` to the EXISTING destination
 * route for the original record. Prefers `source_type + source_id` and only
 * uses the reference as a display fallback. No new pages/APIs — these are all
 * routes that already exist in App.js, and the destination screens still
 * enforce tenant/branch authorization via their own backend calls.
 *
 * Routes that support deep-link row highlighting via `?focus=<id>` today:
 *   /expenses, /sales  (existing scrollIntoView + pulse behavior).
 */
const SOURCE_ROUTE_MAP = {
  expense: '/expenses',
  credit_expense: '/expenses',
  sale: '/sales',
  credit_sale: '/sales',
  pos_order: '/sales',
  bill: '/bills',
  purchase: '/bills',
  supplier_payment: '/suppliers',
  salary: '/salary-report',
  salary_payment: '/salary-report',
  partner_transaction: '/partners',
  partner: '/partners',
  customer_receipt: '/customers',
  stock_transfer: '/transfers',
  transfer: '/transfers',
  settlement: '/inter-branch-ledger',
  inter_branch_settlement: '/inter-branch-ledger',
};

// Destinations that already honor a `?focus=<id>` row highlight.
const FOCUS_ROUTES = new Set(['/expenses', '/sales']);

const SOURCE_LABELS = {
  expense: 'Expense',
  credit_expense: 'Expense',
  sale: 'Sale',
  credit_sale: 'Sale',
  pos_order: 'POS Order',
  bill: 'Bill',
  purchase: 'Purchase',
  supplier_payment: 'Supplier Payment',
  salary: 'Salary Payment',
  salary_payment: 'Salary Payment',
  partner_transaction: 'Partner Transaction',
  partner: 'Partner Transaction',
  customer_receipt: 'Customer Receipt',
  stock_transfer: 'Stock Transfer',
  transfer: 'Stock Transfer',
  settlement: 'Settlement',
  inter_branch_settlement: 'Settlement',
  opening_balance: 'Opening Balance',
};

export const sourceTypeLabel = (t) =>
  SOURCE_LABELS[String(t || '').toLowerCase()] ||
  (t ? String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '');

/**
 * Resolve a navigable path for a source record, or null if the source type
 * has no existing detail destination (then it stays non-clickable).
 */
export function resolveSourceNav(sourceType, sourceId) {
  if (!sourceType) return null;
  const base = SOURCE_ROUTE_MAP[String(sourceType).toLowerCase()];
  if (!base) return null;
  if (sourceId && FOCUS_ROUTES.has(base)) {
    return `${base}?focus=${encodeURIComponent(sourceId)}`;
  }
  return base;
}

export { SOURCE_ROUTE_MAP, FOCUS_ROUTES };
